import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Truck, Trash2, Download, Plus, Star, AlertTriangle, MessageSquare } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { SendToClientButton } from "@/components/common/SendToClientButton";

const STATUS_TONE = {
  Pending: "bg-slate-100 text-slate-600 border-slate-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Conditional: "bg-amber-50 text-amber-700 border-amber-200",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200",
  Suspended: "bg-rose-50 text-rose-700 border-rose-200",
};

const GRADE_TONE = {
  A: "bg-emerald-500 text-white",
  B: "bg-blue-500 text-white",
  C: "bg-amber-500 text-white",
  D: "bg-rose-500 text-white",
};

const empty = {
  client_slug: "", name: "", products_services: "", contact_person: "", contact_email: "", contact_phone: "",
  website: "", address: "", approval_status: "Pending", alternative_supplier: "", review_cycle_months: 12, notes: "",
};

const emptyEval = { date: new Date().toISOString().slice(0, 10), evaluator: "", criteria: [], decision: "Approved", comments: "" };

const StarInput = ({ value, onChange, testid }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`${testid}-star-${n}`}
        onClick={() => onChange(n)}
        className="p-0.5 hover:scale-110 transition-transform"
      >
        <Star className={`h-4 w-4 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

export default function SupplierModal({ open, supplierId, defaultClientSlug, clients = [], onOpenChange, onChanged }) {
  const [form, setForm] = useState(empty);
  const [loaded, setLoaded] = useState(null);
  const [meta, setMeta] = useState({ statuses: ["Pending"], default_criteria: [] });
  const [tab, setTab] = useState("details");
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState({ name: "" });
  const [newEval, setNewEval] = useState(emptyEval);
  const isEdit = !!supplierId;

  useEffect(() => {
    if (!open) return;
    endpoints.supplierMetadata().then((m) => {
      setMeta(m);
      setNewEval({ ...emptyEval, criteria: (m.default_criteria || []).map((c) => ({ ...c, score: 3 })) });
    }).catch(() => {});
    endpoints.me().then(setMe).catch(() => {});
    if (isEdit) {
      endpoints.getSupplier(supplierId).then((s) => { setLoaded(s); setForm({ ...empty, ...s }); setTab("details"); });
    } else {
      setLoaded(null);
      setForm({ ...empty, client_slug: defaultClientSlug || clients[0]?.slug || "" });
      setTab("details");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplierId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error("Supplier name is required");
    if (!form.client_slug) return toast.error("Select a client");
    setSaving(true);
    try {
      if (isEdit) {
        const { client_slug: _cs, ...rest } = form;
        const upd = await endpoints.updateSupplier(supplierId, rest);
        setLoaded(upd);
        toast.success("Supplier updated");
      } else {
        await endpoints.createSupplier(form);
        toast.success("Supplier added");
      }
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${loaded?.name}? This cannot be undone.`)) return;
    setSaving(true);
    try { await endpoints.deleteSupplier(supplierId); toast.success("Deleted"); onChanged?.(); }
    catch { toast.error("Could not delete"); } finally { setSaving(false); }
  };

  const submitEvaluation = async () => {
    if (!isEdit) return toast.error("Save the supplier first");
    if (!newEval.criteria.length) return toast.error("Add at least one criterion");
    setSaving(true);
    try {
      const payload = { ...newEval, evaluator: me.name };
      const upd = await endpoints.addSupplierEvaluation(supplierId, payload);
      setLoaded(upd);
      setForm((f) => ({ ...f, approval_status: upd.approval_status }));
      setNewEval({ ...emptyEval, criteria: (meta.default_criteria || []).map((c) => ({ ...c, score: 3 })), evaluator: me.name });
      toast.success("Evaluation recorded");
      setTab("history");
      onChanged?.();
    } catch { toast.error("Could not save evaluation"); } finally { setSaving(false); }
  };

  const removeEvaluation = async (evId) => {
    if (!window.confirm("Delete this evaluation record?")) return;
    try {
      await endpoints.deleteSupplierEvaluation(supplierId, evId);
      const upd = await endpoints.getSupplier(supplierId);
      setLoaded(upd);
    } catch { toast.error("Could not delete"); }
  };

  const setCriterion = (i, k, v) => setNewEval((e) => ({ ...e, criteria: e.criteria.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const addCriterion = () => setNewEval((e) => ({ ...e, criteria: [...e.criteria, { name: "", weight: 10, score: 3 }] }));
  const removeCriterion = (i) => setNewEval((e) => ({ ...e, criteria: e.criteria.filter((_, idx) => idx !== i) }));

  const computedScore = useMemo(() => {
    const tw = newEval.criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);
    if (!tw) return 0;
    const weighted = newEval.criteria.reduce((s, c) => s + (Number(c.score) || 0) * (Number(c.weight) || 0), 0);
    return Math.round((weighted / tw) / 5 * 100 * 10) / 10;
  }, [newEval.criteria]);

  const evaluations = useMemo(() => (loaded?.evaluations || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")), [loaded]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-supplier" className="sm:max-w-[900px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-md shadow-amber-500/25">
              <Truck className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {isEdit ? (
                  <>
                    <span className="truncate">{loaded?.name || "…"}</span>
                    {loaded && <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[loaded.approval_status] || ""}`}>{loaded.approval_status}</Badge>}
                    {loaded?.rating_grade && <Badge className={`text-[10px] rounded-full ${GRADE_TONE[loaded.rating_grade] || ""}`}>Grade {loaded.rating_grade} · {loaded.current_rating}/100</Badge>}
                  </>
                ) : "New Supplier"}
              </DialogTitle>
              <DialogDescription>
                {isEdit ? `${loaded?.client_name || ""} · ${loaded?.products_services || ""}` : "Add a supplier to this client's approved-supplier register."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="pt-2">
          <TabsList className="bg-slate-100 dark:bg-slate-800 mb-3 h-auto flex-wrap">
            <TabsTrigger value="details" data-testid="sup-tab-details" className="text-xs h-8">Profile</TabsTrigger>
            {isEdit && <TabsTrigger value="history" data-testid="sup-tab-history" className="text-xs h-8">Evaluation history ({evaluations.length})</TabsTrigger>}
            {isEdit && <TabsTrigger value="new-eval" data-testid="sup-tab-new-eval" className="text-xs h-8">Record evaluation</TabsTrigger>}
          </TabsList>

          {tab === "details" && (
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Client</Label>
                  <Select value={form.client_slug} onValueChange={(v) => set("client_slug", v)} disabled={isEdit}>
                    <SelectTrigger data-testid="sup-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Supplier name</Label>
                  <Input data-testid="sup-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div className="col-span-2">
                  <Label>Products / services</Label>
                  <Input data-testid="sup-products-input" value={form.products_services} onChange={(e) => set("products_services", e.target.value)} placeholder="e.g. Freight forwarding, warehousing" />
                </div>
                <div>
                  <Label>Contact person</Label>
                  <Input data-testid="sup-contact-input" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
                </div>
                <div>
                  <Label>Approval status</Label>
                  <Select value={form.approval_status} onValueChange={(v) => set("approval_status", v)}>
                    <SelectTrigger data-testid="sup-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Email</Label><Input data-testid="sup-email-input" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></div>
                <div><Label>Phone</Label><Input data-testid="sup-phone-input" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></div>
                <div><Label>Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." /></div>
                <div><Label>Review cycle (months)</Label><Input type="number" min="1" data-testid="sup-cycle-input" value={form.review_cycle_months} onChange={(e) => set("review_cycle_months", Number(e.target.value))} /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
                <div className="col-span-2"><Label>Alternative supplier</Label><Input data-testid="sup-alt-input" value={form.alternative_supplier} onChange={(e) => set("alternative_supplier", e.target.value)} placeholder="Backup vendor if this one fails" /></div>
                <div className="col-span-2"><Label>Internal notes</Label><Textarea rows={2} data-testid="sup-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {isEdit && (
                  <Button data-testid="sup-delete-button" type="button" variant="ghost" className="text-rose-600 mr-auto" onClick={remove} disabled={saving}>
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                )}
                {isEdit && (
                  <Button asChild variant="outline" size="sm" data-testid="sup-export-pdf-button">
                    <a href={endpoints.exportSupplierPdfUrl(supplierId)} download><Download className="h-3.5 w-3.5 mr-1" /> Export PDF</a>
                  </Button>
                )}
                {isEdit && <SendToClientButton kind="supplier" refId={supplierId} />}
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
                <Button data-testid="sup-submit-button" type="submit" className="brand-gradient text-white" disabled={saving}>
                  {saving ? "Saving..." : isEdit ? "Save changes" : "Add supplier"}
                </Button>
              </DialogFooter>
            </form>
          )}

          {tab === "history" && isEdit && (
            <div className="space-y-3">
              {evaluations.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 p-6 text-center text-xs text-slate-500">
                  No evaluations recorded yet. Use the "Record evaluation" tab to add one.
                </div>
              ) : evaluations.map((e) => {
                const grade = e.overall_score >= 80 ? "A" : e.overall_score >= 60 ? "B" : e.overall_score >= 40 ? "C" : "D";
                return (
                  <div key={e.id} data-testid={`sup-eval-${e.id}`} className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                      <div>
                        <div className="text-xs font-bold">{new Date(e.date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</div>
                        <div className="text-[11px] text-slate-500">Evaluated by {e.evaluator}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`h-9 w-9 rounded-xl flex items-center justify-center font-bold text-sm ${GRADE_TONE[grade]}`}>{grade}</div>
                        <div className="text-right">
                          <div className="text-xl font-extrabold font-display font-mono">{e.overall_score}<span className="text-xs text-slate-500 font-normal">/100</span></div>
                          <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[e.decision] || ""}`}>{e.decision}</Badge>
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeEvaluation(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr><th className="text-left py-1">Criterion</th><th className="text-right py-1 w-24">Weight</th><th className="text-right py-1 w-24">Score /5</th></tr></thead>
                      <tbody>
                        {(e.criteria || []).map((c, i) => (
                          <tr key={i} className="border-t border-slate-100 dark:border-white/5"><td className="py-1.5">{c.name}</td><td className="py-1.5 text-right font-mono">{c.weight}</td><td className="py-1.5 text-right font-mono">{c.score}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    {e.comments && <div className="mt-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-2 text-xs italic"><MessageSquare className="inline h-3 w-3 mr-1 text-slate-400" />{e.comments}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {tab === "new-eval" && isEdit && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Date</Label><Input type="date" value={newEval.date} onChange={(e) => setNewEval((v) => ({ ...v, date: e.target.value }))} /></div>
                <div className="col-span-2">
                  <Label>Decision after evaluation</Label>
                  <Select value={newEval.decision} onValueChange={(v) => setNewEval((e) => ({ ...e, decision: v }))}>
                    <SelectTrigger data-testid="sup-eval-decision-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.statuses || []).filter((s) => s !== "Pending").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Criteria (score 1-5)</Label>
                  <Button type="button" size="sm" variant="ghost" onClick={addCriterion}><Plus className="h-3.5 w-3.5 mr-1" /> Add criterion</Button>
                </div>
                <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr><th className="text-left px-3 py-2">Criterion</th><th className="text-right px-3 py-2 w-24">Weight</th><th className="text-right px-3 py-2 w-40">Score</th><th className="w-8"></th></tr>
                    </thead>
                    <tbody>
                      {newEval.criteria.map((c, i) => (
                        <tr key={i} className="border-t border-slate-100 dark:border-white/5">
                          <td className="px-3 py-2"><Input value={c.name} onChange={(e) => setCriterion(i, "name", e.target.value)} className="h-8" data-testid={`sup-eval-criterion-${i}-name`} /></td>
                          <td className="px-3 py-2 text-right"><Input type="number" min="0" max="100" value={c.weight} onChange={(e) => setCriterion(i, "weight", Number(e.target.value))} className="h-8 w-20 text-right font-mono ml-auto" /></td>
                          <td className="px-3 py-2 text-right"><StarInput testid={`sup-eval-criterion-${i}`} value={Number(c.score) || 0} onChange={(v) => setCriterion(i, "score", v)} /></td>
                          <td><Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeCriterion(i)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 rounded-2xl border border-slate-200 dark:border-white/10 p-3 bg-indigo-50/40 dark:bg-indigo-500/10 flex items-center gap-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Overall score</div>
                  <div data-testid="sup-eval-computed-score" className="text-2xl font-extrabold font-mono brand-gradient-text">{computedScore}<span className="text-xs text-slate-500 ml-1">/100</span></div>
                  <Progress value={computedScore} className="flex-1 h-2" />
                </div>
              </div>
              <div>
                <Label>Comments</Label>
                <Textarea rows={2} data-testid="sup-eval-comments-input" value={newEval.comments} onChange={(e) => setNewEval((v) => ({ ...v, comments: e.target.value }))} placeholder="Observations, incidents, actions to follow up..." />
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
                <Button data-testid="sup-eval-submit-button" type="button" className="brand-gradient text-white" onClick={submitEvaluation} disabled={saving}>
                  {saving ? "Saving..." : "Record evaluation"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
